import React, { useState } from 'react';
import { Mail, MapPin, Building2, Loader2, Send } from 'lucide-react';
import { Layout } from './Home';
import { contactInfo } from '../data/pages';
import { submitContact } from '../services/api';
import { toast } from '../hooks/use-toast';

const INITIAL = { name: '', email: '', phone: '', subject: '', message: '' };

const ContactPage = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await submitContact(form);
      toast({
        title: 'Message sent',
        description: 'Our team will be in touch within one business day.',
      });
      setForm(INITIAL);
    } catch (err) {
      toast({
        title: 'Could not send',
        description:
          err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          'Please check the form and try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="bg-[#e2ede6] pt-14 pb-20 lg:pt-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left: intro + contact info */}
            <div>
              <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Contact</span>
              <h1
                className="mt-3 text-5xl lg:text-6xl text-[#0b1a2e] leading-[1.02]"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
              >
                Let&rsquo;s build something enduring.
              </h1>
              <p className="mt-5 text-[#0b1a2e]/70 leading-relaxed max-w-md">
                Whether you&rsquo;re exploring a partnership, capital solution or a career at
                Emerald, we&rsquo;d love to hear from you.
              </p>

              <div className="mt-10 space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-white border border-[#0f5c3d]/10 flex items-center justify-center text-[#0f5c3d]">
                    <Building2 className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0b1a2e]">{contactInfo.companyName}</div>
                    <p className="text-xs text-[#0b1a2e]/60 mt-0.5">Company No: {contactInfo.companyNumber}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-white border border-[#0f5c3d]/10 flex items-center justify-center text-[#0f5c3d]">
                    <MapPin className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0b1a2e]">Registered Office</div>
                    <p className="text-sm text-[#0b1a2e]/70 leading-relaxed max-w-sm">{contactInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-white border border-[#0f5c3d]/10 flex items-center justify-center text-[#0f5c3d]">
                    <Mail className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#0b1a2e]">Email</div>
                    {contactInfo.emails.map((em) => (
                      <a key={em} href={`mailto:${em}`} className="block text-sm text-[#0b1a2e]/70 hover:text-[#0f5c3d]">
                        {em}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: form */}
            <form
              onSubmit={submit}
              className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 lg:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="block text-xs font-semibold tracking-wide uppercase text-[#0b1a2e]/70 mb-1.5">Full Name</span>
                  <input
                    required
                    value={form.name}
                    onChange={update('name')}
                    className="w-full border border-[#0f5c3d]/15 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                    placeholder="Your name"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-semibold tracking-wide uppercase text-[#0b1a2e]/70 mb-1.5">Email</span>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={update('email')}
                    className="w-full border border-[#0f5c3d]/15 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                    placeholder="you@company.com"
                  />
                </label>
              </div>
              <label className="block">
                <span className="block text-xs font-semibold tracking-wide uppercase text-[#0b1a2e]/70 mb-1.5">Phone (optional)</span>
                <input
                  value={form.phone}
                  onChange={update('phone')}
                  className="w-full border border-[#0f5c3d]/15 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                  placeholder="+91 98xxxxxxxx"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold tracking-wide uppercase text-[#0b1a2e]/70 mb-1.5">Subject</span>
                <input
                  required
                  value={form.subject}
                  onChange={update('subject')}
                  className="w-full border border-[#0f5c3d]/15 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
                  placeholder="How can we help?"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold tracking-wide uppercase text-[#0b1a2e]/70 mb-1.5">Message</span>
                <textarea
                  required
                  value={form.message}
                  onChange={update('message')}
                  rows={5}
                  className="w-full border border-[#0f5c3d]/15 rounded-lg px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30 resize-y"
                  placeholder="Tell us a bit about your requirement…"
                />
              </label>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0f5c3d] text-white font-medium text-sm hover:bg-[#0b1a2e] transition-colors disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
